import { get } from "lodash";
import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment, Header } from "semantic-ui-react";
import { Formik, Form, Field } from "formik";

import * as Yup from "yup";
import * as Schemas from "../forms/validationSchemas.js";

import FormikSemanticInput from "../forms/formik/FormikSemanticInput.jsx";
import FormikTimezoneDropdown from "../forms/time/FormikTimezoneDropdown.jsx";
import FormikActionsPanel from "../forms/formik/FormikActionsPanel.jsx";
import SegmentMessage from "../forms/messages/SegmentMessage.jsx";

import accountSelectors from "../../state/reducers/account/accountSelectors.js";

// Designed to be used with the withBasicForm HOC

class UserDetailsForm extends PureComponent {
  static propTypes = {
    data: PropTypes.object.isRequired,
    onReset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onFormikSubmit: PropTypes.func.isRequired,
    renderMessage: PropTypes.func.isRequired,
    violationErrors: PropTypes.object,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "account.profile"
  };

  constructor(props) {
    super(props);

    this.initialValues = {
      firstName: get(props, "data.firstName", ""),
      lastName: get(props, "data.lastName", ""),
      username: get(props, "data.username", ""),
      timezone: get(props, "data.timezone", props.defaultTimezone)
    };
  }

  // -- VALIDATION -- //

  validationSchema = () => {
    const messages = this.props.copy("forms.validation_requirements");

    return Yup.object().shape({
      firstName: Schemas.simpleStringSchema("First name", messages, true),
      lastName: Schemas.simpleStringSchema("Last name", messages, true),
      // username: Schemas.usernameSchema("Username", messages, true), // cannot currently be updated due to jumpcloud restrictions
      timezone: Schemas.timezoneSchema("Timezone", messages, true)
    });
  };

  onlySubmitDirtyValues = values => {
    const dirtyValues = {};
    Object.keys(values).forEach(key => {
      if (values[key] !== this.initialValues[key]) {
        dirtyValues[key] = values[key];
      }
    });
    this.props.onFormikSubmit(dirtyValues);
  };

  // -- RENDER -- //

  renderTextInput = (name, copyKey, disabled, help) => {
    const { violationErrors, copy } = this.props;

    return (
      <Field
        name={name}
        placeholder={copy(`sentry.register.${copyKey}_input_placeholder`)}
        label={copy(`sentry.register.${copyKey}_input_label`)}
        help={help}
        fluid
        disabled={disabled}
        apiErrors={violationErrors}
        component={FormikSemanticInput}
      />
    );
  };

  renderUserName = () => {
    const { usernameUpdateAvailability, copyKey, copy } = this.props;

    if (!usernameUpdateAvailability) {
      return this.renderTextInput(
        "username",
        "username",
        true,
        copy(`${copyKey}.personal.username_disabled`)
      );
    }

    return this.renderTextInput("username", "username", false);
  };

  renderDetails = () => {
    const { data, detailsUpdateAvailability, copyKey, copy } = this.props;

    if (detailsUpdateAvailability !== true) {
      return (
        <Fragment>
          <SegmentMessage
            info={true}
            message={copy(`${copyKey}.personal.details_disabled`)}
          />
          <p>
            <strong>{`${copy(
              "sentry.register.firstname_input_label"
            )}: `}</strong>
            {data.firstName}
            <br />
            <strong>{`${copy(
              "sentry.register.lastname_input_label"
            )}: `}</strong>
            {data.lastName}
            <br />
            <strong>{`${copy(
              "sentry.register.username_input_label"
            )}: `}</strong>
            {data.username}
          </p>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {this.renderTextInput("firstName", "firstname", false)}
        {this.renderTextInput("lastName", "lastname", false)}
        {this.renderUserName()}
      </Fragment>
    );
  };

  render() {
    const { onReset, onSubmit, renderMessage, violationErrors, copyKey, copy } =
      this.props;

    return (
      <Formik
        initialValues={this.initialValues} // required by semantic-ui react
        validateOnMount={true}
        enableReinitialize={true}
        validationSchema={this.validationSchema()}
        onSubmit={this.onlySubmitDirtyValues}
      >
        {formikProps => {
          return (
            <Segment vertical padded textAlign="left">
              <Form className="ui form">
                {renderMessage(formikProps)}

                <Header as="h3">{copy(`${copyKey}.personal.title`)}</Header>
                {this.renderDetails()}

                <Header as="h3">{copy(`${copyKey}.timezone.title`)}</Header>
                <p>{copy(`${copyKey}.timezone.body`)}</p>

                <Field
                  name={"timezone"}
                  label={`${copy("forms.timezone_dropdown.label")}`}
                  placeholder={`${copy("forms.timezone_dropdown.placeholder")}`}
                  disabled={false}
                  required={true}
                  apiErrors={violationErrors}
                  component={FormikTimezoneDropdown}
                />

                <FormikActionsPanel
                  reset={true}
                  onReset={onReset}
                  submit={true}
                  useSubmitModal={true}
                  onSubmit={onSubmit}
                  modalCopy={copy(`account.modals`)}
                />
              </Form>
            </Segment>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = state => ({
  defaultTimezone: get(state, "account.timezone", {}),
  usernameUpdateAvailability:
    accountSelectors.selectUsernameUpdateAvailability(state),
  detailsUpdateAvailability:
    accountSelectors.selectDetailsUpdateAvailability(state)
});

export default connect(mapStateToProps)(UserDetailsForm);
